/**
 * WHEN the grid speaks. The hard half of a live region is not the wording —
 * it is refusing to fire.
 *
 * ── THE TRIGGER ────────────────────────────────────────────────────────
 *
 *   settled criteria change          rows recomputed
 *   (filters · search · quick)  ──►  since that change   ──►  ONE utterance
 *          │                               │
 *          │                               └ the "data caught up" signal
 *          └ `useGridRows` hands back DEBOUNCED values, so this is already
 *            the settled search text, never a keystroke
 *
 * ── WHY "ROWS RECOMPUTED" AND NOT `loading` ────────────────────────────
 *
 * `useServerEnhancedGridQuery.ts:43` reports `loading: loading && !data`, so
 * a paged REFETCH never raises the flag — by design, so the no-rows overlay
 * can render instead of a skeleton. Announcing on the criteria change alone
 * would therefore read out the OLD count with the NEW filters: not noise, a
 * lie. The row array's identity is the honest signal in both modes:
 *
 *   mode="local"   `filteredData` is a useMemo over the criteria  ─► same commit
 *   mode="paged"   Apollo hands back a new array with new data    ─► later commit
 *
 * ── WHAT DELIBERATELY DOES NOT ANNOUNCE ────────────────────────────────
 *
 *   first render          the user has done nothing yet
 *   pagination            rows change, criteria did not
 *   sorting               same
 *   a row edit / refetch  same
 *   a re-render           nothing changed at all
 */
import type { GridValidRowModel } from '@mui/x-data-grid-premium';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { BaseGridState } from '../shared/baseGridTypes';
import type { ServerGridFilter } from '../shared/gridFilterTypes';
import {
  type GridAnnouncement,
  type GridResultCriteria,
  gridCriteriaChanged,
  gridResultAnnouncement,
  gridResultCriteria,
  isAppliedFilter,
  nextAnnouncement,
  SILENT_ANNOUNCEMENT,
} from './gridAnnouncements';

export type GridResultAnnouncementOptions = {
  /**
   * The SETTLED grid state. Absent on a plain grid that opted into no mode —
   * there are no criteria to announce, so the region stays silent.
   */
  enhancedGridState?: Pick<BaseGridState, 'gridFilters' | 'searchText' | 'quickFilterOption'>;
  /** Column definitions, read only to skip page-pinned (`locked`) filters. */
  columns?: AnnouncementColumns;
  /** The rows the grid is rendering. IDENTITY is the signal; contents are not read. */
  rows?: readonly GridValidRowModel[];
  /** MUI's `rowCount` — the server total on a paged grid; `-1`/absent when unknown. */
  rowCount?: number;
  loading?: boolean;
};

type AnnouncementColumns = Record<string, { customFilterType?: string } | undefined>;

const EMPTY_FILTERS: readonly ServerGridFilter[] = [];

/**
 * The filters a user would say they applied: carrying a value, and not pinned
 * by the page. Same two exclusions `NoRowsOverlay` makes when it decides
 * whether to offer "clear them all".
 */
const announceableFilters = (filters: readonly ServerGridFilter[], columns?: AnnouncementColumns): ServerGridFilter[] =>
  filters.filter((filter) => isAppliedFilter(filter) && columns?.[filter.field]?.customFilterType !== 'locked');

export function useGridResultAnnouncement({
  enhancedGridState,
  columns,
  rows,
  rowCount,
  loading,
}: GridResultAnnouncementOptions): GridAnnouncement {
  const filters = enhancedGridState?.gridFilters ?? EMPTY_FILTERS;
  const searchText = enhancedGridState?.searchText ?? '';
  const quickFilterLabel = enhancedGridState?.quickFilterOption?.label ?? '';

  const criteria = useMemo(
    () => gridResultCriteria(announceableFilters(filters, columns), searchText, quickFilterLabel),
    [filters, columns, searchText, quickFilterLabel]
  );

  /** The server total when the server gave one, else what is on screen. */
  const resultCount = typeof rowCount === 'number' && rowCount >= 0 ? rowCount : (rows?.length ?? 0);

  const [announcement, setAnnouncement] = useState(SILENT_ANNOUNCEMENT);

  const lastCriteria = useRef(criteria);
  const lastRows = useRef(rows);
  const wasLoading = useRef(!!loading);
  /** The criteria we were showing when the pending change started — `null` when nothing is owed. */
  const pendingFrom = useRef<GridResultCriteria | null>(null);

  useEffect(() => {
    const rowsChanged = rows !== lastRows.current;
    const loadingSettled = wasLoading.current && !loading;
    const previous = lastCriteria.current;

    lastRows.current = rows;
    wasLoading.current = !!loading;

    if (gridCriteriaChanged(previous, criteria)) {
      lastCriteria.current = criteria;
      // Keep the OLDEST un-announced snapshot: two changes inside one fetch are
      // one thing that happened to the user, and get one sentence.
      pendingFrom.current = pendingFrom.current ?? previous;
    }

    if (pendingFrom.current === null || loading || !(rowsChanged || loadingSettled)) {
      return;
    }

    const from = pendingFrom.current;
    pendingFrom.current = null;

    const text = gridResultAnnouncement(from, criteria, resultCount);

    // Criteria that changed and changed BACK inside one fetch leave nothing to
    // say; bumping the counter would speak the previous sentence a second time.
    if (text === '') return;

    setAnnouncement((previous) => nextAnnouncement(previous, text));
  }, [criteria, rows, resultCount, loading]);

  return announcement;
}
